import { useState, useEffect } from 'react';
import { ChevronLeft, Save, Truck } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';
import { parseTrailers } from '../../lib/trailers';

// Red border when a field has a validation message, normal styling otherwise.
function fieldClass(error?: string) {
  return `w-full px-4 py-2 border rounded-lg focus:ring-2 ${
    error
      ? 'border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500'
      : 'border-slate-300 focus:ring-blue-500 focus:border-blue-500'
  }`;
}

const errorClass = 'mt-1 text-sm text-red-600';

export function CreateTruckPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompanies();
    loadDispatchers();
  }, []);

  async function loadCompanies() {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  async function loadDispatchers() {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('role', ['Dispatch', 'Dispatch Manager', 'Admin', 'Superadmin'])
        .order('full_name');
      
      if (error) throw error;
      setDispatchers(data || []);
    } catch (error) {
      console.error('Error loading dispatchers:', error);
    }
  }

  const [unitNumber, setUnitNumber] = useState('');
  const [vin, setVin] = useState('');
  const [make, setMake] = useState('');
  const [year, setYear] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [registrationExpiration, setRegistrationExpiration] = useState('');
  const [dotExpiration, setDotExpiration] = useState('');
  const [prepass, setPrepass] = useState('');
  const [ifta2024, setIfta2024] = useState('');
  const [ifta2025, setIfta2025] = useState(false);
  const [ifta2026, setIfta2026] = useState(false);
  const [ny2025, setNy2025] = useState(false);
  const [samsaraEnabled, setSamsaraEnabled] = useState<'Yes' | 'No' | 'Prijavljena'>('Yes');
  const [samsaraVehicleId, setSamsaraVehicleId] = useState('');
  const [trailers, setTrailers] = useState('');
  const [truckStatus, setTruckStatus] = useState('ACTIVE');
  const [companyId, setCompanyId] = useState('');
  const [notes, setNotes] = useState('');
  const [dispatcherId, setDispatcherId] = useState('');
  
  // File Uploads
  const [registrationFile, setRegistrationFile] = useState<File | null>(null);
  const [dotFile, setDotFile] = useState<File | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const [companies, setCompanies] = useState<any[]>([]);
  const [dispatchers, setDispatchers] = useState<any[]>([]);

  // Per-field messages shown under the inputs. Keyed by state name.
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Drop a field's message as soon as the user starts correcting it.
  const clearError = (field: string) =>
    setErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));

  // Returns a field -> message map; empty means the form is good to submit.
  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    const thisYear = new Date().getFullYear();

    if (!unitNumber.trim()) next.unitNumber = 'Unit Number is required';

    if (!vin.trim()) {
      next.vin = 'VIN is required';
    } else if (vin.trim().length !== 17) {
      next.vin = `VIN must be 17 characters (you entered ${vin.trim().length})`;
    }

    if (!make.trim()) next.make = 'Make is required';

    if (!year.trim()) {
      next.year = 'Year is required';
    } else if (!/^\d{4}$/.test(year.trim())) {
      next.year = 'Year must be 4 digits, e.g. 2025';
    } else if (Number(year) < 1950 || Number(year) > thisYear + 2) {
      next.year = `Year must be between 1950 and ${thisYear + 2}`;
    }

    return next;
  }

  // Turns a Postgres/PostgREST error into something a dispatcher can act on.
  function describeError(error: any): string {
    const code = error?.code;
    const detail: string = error?.message || '';

    if (code === '23505') {
      if (detail.includes('unit_number')) {
        return `Unit Number "${unitNumber}" already exists. Use a different unit number.`;
      }
      return 'A truck with these details already exists.';
    }
    if (code === '23503') {
      return 'The selected Company or Dispatcher no longer exists. Reselect it and try again.';
    }
    if (code === '23502') {
      return 'A required field was left empty. Please fill in all fields marked with *.';
    }
    if (code === '22P02') {
      return `Invalid value for one of the fields: ${detail}`;
    }
    if (code === 'PGRST204') {
      return `This form is out of sync with the database (${detail}). Tell an admin.`;
    }
    if (code === '42501' || detail.toLowerCase().includes('row-level security')) {
      return 'You do not have permission to add trucks. Ask an admin to check your role.';
    }
    return detail || 'Unknown error';
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const labels: Record<string, string> = {
        unitNumber: 'Unit Number',
        vin: 'VIN',
        make: 'Make',
        year: 'Year',
      };
      const list = Object.keys(found).map(k => labels[k] || k).join(', ');
      toast.error(`Cannot save — please fix: ${list}`);
      // Bring the first problem field into view.
      document.querySelector('[data-error="true"]')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    setSaving(true);

    try {
      // Upload files if selected
      let registrationFileUrl = '';
      let dotFileUrl = '';

      if (registrationFile || dotFile) {
        setUploadingFiles(true);
        
        // Upload registration file
        if (registrationFile) {
          const fileExt = registrationFile.name.split('.').pop();
          const fileName = `registration_${unitNumber}_${Date.now()}.${fileExt}`;
          const filePath = `truck-documents/${unitNumber}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, registrationFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('company-documents')
            .getPublicUrl(filePath);

          registrationFileUrl = publicUrl;
        }

        // Upload DOT file
        if (dotFile) {
          const fileExt = dotFile.name.split('.').pop();
          const fileName = `dot_${unitNumber}_${Date.now()}.${fileExt}`;
          const filePath = `truck-documents/${unitNumber}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('company-documents')
            .upload(filePath, dotFile, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('company-documents')
            .getPublicUrl(filePath);

          dotFileUrl = publicUrl;
        }

        setUploadingFiles(false);
      }

      const { error } = await supabase.from('trucks').insert({
        unit_number: unitNumber,
        vin: vin,
        make: make,
        model: make,
        year: parseInt(year),
        license_plate: licensePlate,
        registration_expiration: registrationExpiration || null,
        dot_expiration: dotExpiration || null,
        prepass: prepass,
        ifta_2024: ifta2024,
        ifta_2025: ifta2025,
        ifta_2026: ifta2026,
        ny_2025: ny2025,
        samsara_enabled: samsaraEnabled,
        samsara_vehicle_id: samsaraVehicleId || null,
        trailers: parseTrailers(trailers),
        truck_status: truckStatus,
        status: 'in_service',
        ownership: 'company',
        company_id: companyId || null,
        notes: notes || null,
        dispatcher_id: dispatcherId || null,
        registration_file_url: registrationFileUrl || null,
        dot_file_url: dotFileUrl || null,
      });

      if (error) throw error;

      toast.success('Truck created successfully!');
      navigate('/fleet');
    } catch (error) {
      console.error('Error creating truck:', error);
      toast.error(`Could not save truck — ${describeError(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/fleet')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">Add Truck</h1>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/fleet')}
                className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Truck'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Unit Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={unitNumber}
                  onChange={(e) => { setUnitNumber(e.target.value); clearError('unitNumber'); }}
                  data-error={!!errors.unitNumber}
                  className={fieldClass(errors.unitNumber)}
                  required
                />
                {errors.unitNumber && <p className={errorClass}>{errors.unitNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  VIN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => { setVin(e.target.value); clearError('vin'); }}
                  data-error={!!errors.vin}
                  className={fieldClass(errors.vin)}
                  required
                />
                {errors.vin && <p className={errorClass}>{errors.vin}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Make <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={make}
                  onChange={(e) => { setMake(e.target.value); clearError('make'); }}
                  placeholder="e.g., FREIGHTLINER"
                  data-error={!!errors.make}
                  className={fieldClass(errors.make)}
                  required
                />
                {errors.make && <p className={errorClass}>{errors.make}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Year <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => { setYear(e.target.value); clearError('year'); }}
                  data-error={!!errors.year}
                  className={fieldClass(errors.year)}
                  required
                />
                {errors.year && <p className={errorClass}>{errors.year}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  value={licensePlate}
                  onChange={(e) => setLicensePlate(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  PrePass
                </label>
                <input
                  type="text"
                  value={prepass}
                  onChange={(e) => setPrepass(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Trailers
                </label>
                <input
                  type="text"
                  value={trailers}
                  onChange={(e) => setTrailers(e.target.value)}
                  placeholder="Trailer unit numbers"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Truck Status
                </label>
                <select
                  value={truckStatus}
                  onChange={(e) => setTruckStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ALERT">Alert - FC, ne voze, shop na putu, medical itd.</option>
                  <option value="POTENCIJALNI RECOVERY">Potencijalni Recovery</option>
                  <option value="RECOVERY - 100%">Recovery - 100%</option>
                  <option value="COMING BACK">Coming Back</option>
                  <option value="JARDA - AVAILABLE">Jarda - Available</option>
                  <option value="JARDA - VACATION">Jarda - Vacation</option>
                  <option value="SHOP/NA YARDI - nasi kamioni">Shop/Na Yardi - nasi kamioni</option>
                  <option value="SHOP OWNERI">Shop Owneri</option>
                  <option value="VACATION - ALI NISU NA JARDI PARKIRANI">Vacation - ali nisu na jardi parkirani</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company
                </label>
                <select
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Dispatcher
                </label>
                <select
                  value={dispatcherId}
                  onChange={(e) => setDispatcherId(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">No Dispatcher</option>
                  {dispatchers.map((dispatcher) => (
                    <option key={dispatcher.id} value={dispatcher.id}>
                      {dispatcher.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Samsara Enabled
                </label>
                <select
                  value={samsaraEnabled}
                  onChange={(e) => setSamsaraEnabled(e.target.value as 'Yes' | 'No' | 'Prijavljena')}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Prijavljena">Prijavljena</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Samsara Vehicle ID
                </label>
                <input
                  type="text"
                  value={samsaraVehicleId}
                  onChange={(e) => setSamsaraVehicleId(e.target.value)}
                  placeholder="e.g., 281474980876049"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Link this truck to a Samsara vehicle for GPS tracking</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this truck..."
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Expiration Dates */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Expiration Dates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Registration Expiration
                </label>
                <input
                  type="date"
                  value={registrationExpiration}
                  onChange={(e) => setRegistrationExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  DOT Expiration
                </label>
                <input
                  type="date"
                  value={dotExpiration}
                  onChange={(e) => setDotExpiration(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Document Uploads</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Registration File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Registration File</label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setRegistrationFile(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {registrationFile && (
                    <div className="text-sm text-slate-600">
                      Selected: {registrationFile.name}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                </div>
              </div>

              {/* DOT File */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">DOT File</label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setDotFile(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {dotFile && (
                    <div className="text-sm text-slate-600">
                      Selected: {dotFile.name}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Accepts PDF and images (JPG, PNG)</p>
                </div>
              </div>
            </div>
          </div>

          {/* IFTA & Compliance */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">IFTA & Compliance</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  IFTA 2024
                </label>
                <input
                  type="text"
                  value={ifta2024}
                  onChange={(e) => setIfta2024(e.target.value)}
                  placeholder="e.g., 004583 Apollo 2025"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ifta2025}
                    onChange={(e) => setIfta2025(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">IFTA 2025</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ifta2026}
                    onChange={(e) => setIfta2026(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">IFTA 2026</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ny2025}
                    onChange={(e) => setNy2025(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">NY 2025</span>
                </label>
              </div>

            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
